<template>
	<v-row align-content="start" justify="start">
		<v-col cols="12">
			<v-card class="d-flex flex-column pb-5">
				<v-overlay
					:model-value="loading"
					persistent
					contained
					class="align-center justify-center"
				>
					<v-progress-circular indeterminate color="primary" size="48" />
				</v-overlay>
				<v-toolbar color="primary" dark flat>
					<v-toolbar-title style="flex: 0 1 auto">
						Dactyl Concepts
					</v-toolbar-title>
					<!-- One search field, contextual to the active tab: on a concept-type
					     tab it live-filters that tab's list; on the NCI EVS Search tab it
					     is a search-on-Enter EVS lookup (hence @keyup.enter). The
					     placeholder names the current context. -->
					<v-text-field
						v-model="searchTerm"
						prepend-inner-icon="mdi-text-search-variant"
						:placeholder="searchPlaceholder"
						density="compact"
						variant="solo"
						flat
						rounded
						hide-details
						clearable
						bg-color="white"
						class="flex-grow-0 ml-4 meta-toolbar-search"
						@keyup.enter="handleSearch"
					/>
					<!-- Terminology scope for the EVS search tab: pick one vocabulary or
					     search across all supported ones. Shown only on the EVS tab; the
					     chosen value rides into handleSearch and, per hit, gates which
					     Dactyl axis it may be added as (medrt -> moa only). -->
					<v-select
						v-if="isEvsTab"
						v-model="terminology"
						:items="terminologyOptions"
						density="compact"
						variant="solo"
						flat
						rounded
						hide-details
						bg-color="white"
						label="Terminology"
						class="flex-grow-0 ml-2 meta-terminology-select"
						@update:model-value="onTerminologyChange"
					/>
					<template #extension>
						<!-- All tabs share one v-model but read as three groups, split
						     by dividers: the Normalization scope concept, the
						     characterization axes (orange accent), and the external
						     sources (teal accent + persistently-tinted icon). Each tab's
						     `group` drives the dividers (one appears wherever consecutive
						     tabs change group) and its `external` flag drives the accent,
						     so adding a future axis or source (UMLS/RxNorm) is a one-line
						     `tabs` addition. -->
						<v-tabs
							v-model="currentTab"
							bg-color="transparent"
							color="secondary"
							align-tabs="title"
							selected-class="tabs-slider"
						>
							<template v-for="(tab, i) in tabs" :key="tab.name">
								<!-- A divider wherever the group changes between
								     neighbouring tabs (normalization | axes | external). -->
								<v-divider
									v-if="i > 0 && tab.group !== tabs[i - 1].group"
									vertical
									class="mx-2 my-3 tab-group-divider"
								/>
								<v-tab
									:value="i"
									:color="tab.external ? externalAccent : 'secondary'"
									class="font-weight-bold"
								>
									<v-icon
										:icon="tab.icon"
										:color="tab.external ? externalAccent : undefined"
										start
									/>
									{{ tab.name }}
								</v-tab>
							</template>
						</v-tabs>
					</template>
				</v-toolbar>

				<v-tabs-window v-model="currentTab">
					<!-- One tab per concept type; each shows that type's concepts,
					     filtered client-side off the serialized `type`. -->
					<!-- Transitions off: the slide animates the pane while its list is
					     remounting, so LazyRender attaches its IntersectionObservers
					     against a mid-transition layout and mis-marks the top rows as
					     off-screen - they then never hydrate (an observer only fires on a
					     later intersection *change*, which a settled row never triggers).
					     Mounting into the final layout keeps the observers correct. -->
					<v-tabs-window-item
						v-for="(tab, i) in typeTabs"
						:key="tab.type"
						:value="i"
						:transition="false"
						:reverse-transition="false"
					>
						<!-- Only the active tab's list is mounted. Vuetify's
						     v-tabs-window otherwise boots each visited tab and keeps it in
						     the DOM (display:none), so every visited concept list would
						     coexist and each switch would recalc a growing node set; the
						     v-if tears down inactive lists so only one is ever live. -->
						<dactyl-concept-list
							v-if="currentTab === i"
							:concepts="filteredActiveConcepts"
							:concept-type="tab.type"
							@edit="openEdit"
							@create="openCreate(tab.type)"
							@open-evs="openEvsForCode"
							@changed="fetchConcepts"
						/>
					</v-tabs-window-item>

					<v-tabs-window-item
						:value="typeTabs.length"
						:transition="false"
						:reverse-transition="false"
					>
						<evs-results-list
							:concepts="evsResults"
							:search-term="searchedTerm"
							:concepts-by-nci="conceptsByNci"
							@add="openAdd"
							@edit="openEditFromEvs"
						/>
					</v-tabs-window-item>
				</v-tabs-window>
			</v-card>
		</v-col>

		<v-dialog
			v-model="editorOpen"
			max-width="620px"
			transition="fade-transition"
		>
			<concept-editor
				v-if="editorOpen"
				:mode="editorMode"
				:initial-data="editorData"
				:nci-linkable="!editorFromEvs"
				@dialog-close="editorOpen = false"
				@open-evs="openEvsForCode"
				@saved="onEditorSaved"
			/>
		</v-dialog>
	</v-row>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import EvsResultsList from "./MetathesaurusResultsList.vue";
import DactylConceptList from "./DactylConceptList.vue";
import ConceptEditor from "./ConceptEditor.vue";
import { EVS_TERMINOLOGY_OPTIONS } from "@/constants/sourceAxisRules";

export default {
	name: "DactylConceptsBrowser",
	components: {
		EvsResultsList,
		DactylConceptList,
		ConceptEditor,
	},

	data() {
		return {
			currentTab: 0,
			searchTerm: "",
			searchedTerm: "",
			// EVS terminology scope for the search tab. Defaults to ncim (the legacy
			// single-vocabulary behavior); the user can widen to All or pick medrt
			// (MoA) / ncit. Passed to /metathesaurus/search and used per-hit to gate
			// which Dactyl axis a result may be added as.
			terminology: "ncim",
			terminologyOptions: EVS_TERMINOLOGY_OPTIONS,
			evsResults: [],
			concepts: [],
			loading: false,
			editorOpen: false,
			editorMode: "add",
			editorData: {},
			// True when the editor was opened from the NCI EVS Search tab. In that
			// context the NCI-id link is suppressed (the user is already viewing the
			// EVS entry) - see item 4 of the concepts-polish pass.
			editorFromEvs: false,
			// Accent (Vuetify theme color) for external-source tabs - a teal that
			// reads as clearly "other" against the blue toolbar and the orange
			// (secondary) native accent. Drives both the active-tab underline and the
			// always-on icon tint.
			externalAccent: "cyan-lighten-2",
			// One tab per concept type (each a type-filtered list), then the NCI EVS
			// search. A tab with a `type` is a native concept list; a tab with
			// `external: true` (type null) hits an off-platform source. Types mirror
			// DACTYL_CONCEPT_TYPES on the backend. `group` clusters the tabs for the
			// tab-bar dividers (a divider renders wherever the group changes).
			// Icons are a first pass (mdi); refine as needed.
			tabs: [
				{
					name: "Normalization",
					type: "normalization",
					group: "normalization",
					icon: "mdi-tune-variant",
					description: "Concepts that hold normalization scope",
				},
				{
					name: "Disease",
					type: "disease",
					group: "characterization",
					icon: "mdi-virus-outline",
					description: "Disease / condition characterization concepts",
				},
				{
					name: "Anatomy",
					type: "anatomy",
					group: "characterization",
					icon: "mdi-human",
					description: "Anatomy / body-system characterization concepts",
				},
				{
					name: "Drug class",
					type: "drug_class",
					group: "characterization",
					icon: "mdi-pill",
					description: "Drug-class characterization concepts",
				},
				{
					name: "Mechanism of action",
					type: "moa",
					group: "characterization",
					icon: "mdi-molecule",
					description: "Mechanism-of-action characterization concepts",
				},
				{
					name: "NCI EVS Search",
					type: null,
					external: true,
					group: "external",
					icon: "mdi-cloud-search-outline",
					description: "Search NCI EVS and add concepts to Dactyl Concepts",
				},
			],
		};
	},

	computed: {
		// The concept-list tabs (everything except the EVS search), in order.
		typeTabs() {
			return this.tabs.filter((tab) => tab.type);
		},
		// The NCI EVS Search tab is the one with no concept type.
		isEvsTab() {
			return this.tabs[this.currentTab]?.type == null;
		},
		// Contextual search placeholder: NCI EVS on the search tab, otherwise the
		// current concept-type list.
		searchPlaceholder() {
			if (this.isEvsTab) return "Search NCI EVS...";
			const name = this.tabs[this.currentTab]?.name ?? "Concepts";
			return `Search Dactyl Concepts (${name})...`;
		},
		// Map NCI CUI -> added Dactyl concept, for marking NCI EVS results. A
		// concept can roll up several CUIs (DCTL-2024), so every one of its CUIs
		// maps back to it (fall back to the single denormalized CUI for back-compat).
		conceptsByNci() {
			const map = {};
			for (const concept of this.concepts) {
				const cuis =
					Array.isArray(concept.nciConceptIds) && concept.nciConceptIds.length
						? concept.nciConceptIds
						: concept.nciConceptId
							? [concept.nciConceptId]
							: [];
				for (const cui of cuis) {
					map[cui] = concept;
				}
			}
			return map;
		},
		// Concepts grouped by type, computed once off the single fetched list (a
		// missing type reads as normalization - every pre-5c.1 concept). Cached so
		// the O(n) split runs only when the fetched list changes, not on every
		// re-render / keystroke.
		conceptsByType() {
			const map = {};
			for (const tab of this.typeTabs) map[tab.type] = [];
			for (const concept of this.concepts) {
				const type = concept.type || "normalization";
				(map[type] || (map[type] = [])).push(concept);
			}
			return map;
		},
		// The active concept tab's type (null on the NCI EVS Search tab).
		activeType() {
			return this.tabs[this.currentTab]?.type ?? null;
		},
		// The active tab's concepts, live-filtered by the shared search box. Only
		// the active list is rendered (inactive tabs are torn down via v-if), so
		// this runs for one type and is cached - replacing the old per-tab
		// conceptsOfType() method that re-ran for every booted tab on every
		// keystroke.
		filteredActiveConcepts() {
			const type = this.activeType;
			if (!type) return [];
			const list = this.conceptsByType[type] || [];
			const q = (this.searchTerm || "").trim().toLowerCase();
			if (!q) return list;
			return list.filter((concept) => {
				// Match on the preferred name, any synonym, the DACTYL id, and any
				// source id (NCI CUI) - so a search / the NCI-link round-trip that
				// leaves an id like "C35094" or "DACTYL0000345" in the box still
				// finds its concept rather than showing an empty tab.
				const haystack = [
					concept.preferredName,
					concept.dactylConceptId,
					concept.nciConceptId,
					...(concept.nciConceptIds || []),
					...(concept.synonyms || []).map((s) => s.synonym),
				];
				return haystack.some(
					(value) => value && String(value).toLowerCase().includes(q),
				);
			});
		},
	},

	mounted() {
		this.fetchConcepts();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		fetchConcepts() {
			this.$http
				.post("/concepts/list", {})
				.then(({ data: responseData }) => {
					this.concepts = responseData.data || [];
				})
				.catch((error) => {
					// This is the sole data source for the Dactyl Concepts tab and
					// the added-status lookup, so a silent failure would masquerade
					// as an empty store. Surface it.
					console.error("Failed to fetch Dactyl concepts:", error);
					this.showNotification({
						status: "error",
						message: "Failed to load Dactyl concepts",
					});
				});
		},
		handleSearch() {
			// Enter only triggers an EVS query on the NCI EVS Search tab; on a concept
			// tab the search field is a live client-side filter (conceptsOfType).
			if (!this.isEvsTab) return;
			this.loading = true;
			this.$http
				.post("/metathesaurus/search", {
					key: this.searchTerm?.toLowerCase() ?? "",
					terminology: this.terminology,
				})
				.then(({ data: responseData }) => {
					this.searchedTerm = this.searchTerm;
					this.evsResults = responseData?.data?.evs ?? [];
					this.loading = false;
				})
				.catch((error) => {
					console.error(error);
					this.loading = false;
				});
		},
		// Re-run the search when the terminology changes, so switching vocabulary
		// refreshes results in place (only if there's already a submitted search).
		onTerminologyChange() {
			if (this.isEvsTab && this.searchTerm?.trim()) this.handleSearch();
		},
		openAdd(evsConcept) {
			this.editorMode = "add";
			this.editorFromEvs = true;
			this.editorData = {
				nciConceptId: evsConcept.code,
				preferredName: evsConcept.name,
				// Each hit carries its source terminology (from EVS, or stamped by the
				// backend). It gates which Dactyl axis this concept may be added as, and
				// selects the code system it anchors under. Fall back to the tab's
				// current terminology if a hit somehow lacks one.
				sourceTerminology: evsConcept.terminology || this.terminology,
				synonyms: (evsConcept.synonyms || []).map((syn) => ({
					synonym: syn.name,
					source: syn.source ?? null,
				})),
			};
			this.editorOpen = true;
		},
		openEdit(concept) {
			this.editorMode = "edit";
			this.editorFromEvs = false;
			this.editorData = concept;
			this.editorOpen = true;
		},
		// Edit opened from the NCI EVS Search tab: same as openEdit, but the editor
		// suppresses the NCI-id link since the user is already on the EVS entry.
		openEditFromEvs(concept) {
			this.editorMode = "edit";
			this.editorFromEvs = true;
			this.editorData = concept;
			this.editorOpen = true;
		},
		// Create a wholly-local concept of the originating tab's type (the type is
		// fixed here - you clicked "New Disease" - so the editor shows it read-only).
		openCreate(type) {
			this.editorMode = "create";
			this.editorFromEvs = false;
			this.editorData = { type: type || "normalization" };
			this.editorOpen = true;
		},
		// Jump to the NCI EVS Search tab prefilled with a source code and run the
		// search, so a concept's source id links back to its EVS entry. The optional
		// terminology scopes the search to the right vocabulary (medrt for a MoA/MED-RT
		// code, else ncim); defaults to ncim for the legacy NCI link-back.
		openEvsForCode(code, terminology) {
			if (!code) return;
			this.editorOpen = false;
			const evsTab = this.tabs.findIndex((t) => t.name === "NCI EVS Search");
			if (evsTab !== -1) this.currentTab = evsTab;
			this.terminology = terminology || "ncim";
			this.searchTerm = code;
			this.handleSearch();
		},
		onEditorSaved() {
			this.editorOpen = false;
			this.fetchConcepts();
		},
	},
};
</script>

<style scoped lang="scss">
.col {
	flex-grow: 0;
}

.meta-toolbar-search {
	min-width: 390px;
	max-width: 630px;
}

// The terminology scope selector sits beside the search field on the EVS tab; give
// it a fixed comfortable width so the vocabulary names don't truncate.
.meta-terminology-select {
	min-width: 210px;
	max-width: 240px;
}

// A default vertical v-divider is nearly invisible on the blue toolbar. Lift its
// contrast so the tab groups read as separated.
.tab-group-divider {
	border-color: rgba(255, 255, 255, 0.5);
	opacity: 1;
}
</style>
